import { Component, OnInit } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatSelGuidelinesService } from "src/app/services/mat-sel-guidelines/mat-sel-guidelines.service";
import { UnitMeasureService } from "src/app/services/unit-measures.service";
import {
  PRESSURE_UNITS_KPA_ARRAY,
  PRESSURE_UNITS_KSI_ARRAY,
} from "src/app/shared/consts/pressure-units";
import { TEMPERATURE_UNITS_ARRAY } from "src/app/shared/consts/temperature-units";
import { PERCENTAGE_UNITS_ARRAY } from "src/app/shared/consts/percentage-units";
import { PressureUnits } from "src/app/shared/enums/pressure-units.enum";
import { PercentageUnits } from "src/app/shared/enums/percentage-units.enum";
import Swal from "sweetalert2";
import { TemperatureUnits } from "src/app/shared/enums/temperature-units.enum";
import { UnitMeasures } from "src/app/shared/enums/unit-measures.enum";
import { Subject, combineLatest, merge } from "rxjs";
import { RunModelCraRequest } from "../models/cra";
import { debounceTime, pairwise, startWith } from "rxjs/operators";
import { MatSelGuidelinesComponentsService } from "../services/mat-sel-guidelines-components.service";
import { PreSimulationService } from "src/app/shared/components/pre-simulation/service/pre-simulation.service";
import { REGEXP_PATTERNS } from "src/app/shared/consts/regexp-patterns";
import { InterchaneableUnitValidator } from "src/app/shared/validators/interchangeable-unit.validator";
import { NumericInputValidator } from "src/app/shared/validators/numeric-input.validator";

@Component({
  selector: "app-cra",
  templateUrl: "./cra.component.html",
  styleUrls: ["./cra.component.css"],
})
export class CraComponent implements OnInit {
  fg: UntypedFormGroup;

  grades: string[] = [];
  gradesFiltered: string[] = [];

  readonly TEMPERATURE_UNITS_ARRAY = TEMPERATURE_UNITS_ARRAY;
  readonly PRESSURE_UNITS_KPA_ARRAY = PRESSURE_UNITS_KPA_ARRAY;
  readonly PRESSURE_UNITS_KSI_ARRAY = PRESSURE_UNITS_KSI_ARRAY;

  readonly PERCENTAGE_UNITS_ARRAY = PERCENTAGE_UNITS_ARRAY;


  h2sUnit: UntypedFormControl = new UntypedFormControl(PercentageUnits.Percent);
  unitH2S: string;
  defaultH2s: number;

  temperatureUnit: UntypedFormControl = new UntypedFormControl(TemperatureUnits.Celsius);
  totalPressureUnit: UntypedFormControl = new UntypedFormControl(PressureUnits.Kpa);
  minYSRequiredUnit: UntypedFormControl = new UntypedFormControl(PressureUnits.Ksi);
  oPph2s: any;
  unitPPH2S: string;
  defaultOPph2s: number;
  model: any;
  isLoading: boolean = false;

  private minYSSubject = new Subject<number | undefined>();

  rangeCra: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    public unitMeasureService: UnitMeasureService,
    private matSelGuidelinesService: MatSelGuidelinesService,
    private matSelGuidelinesComponentsService: MatSelGuidelinesComponentsService,
    private preSimulationService: PreSimulationService
  ) {}

  ngOnInit(): void {
    this.fg = this.fb.group({
      temperature: [
        null,
        [
          Validators.required,
          Validators.pattern(REGEXP_PATTERNS.SIGNED_NUMBER_4_DECIMALS),
          InterchaneableUnitValidator.delimited(
            20,
            250,
            this.temperatureUnit,
            UnitMeasures.Temperature,
            this.unitMeasureService
          ),
        ],
      ],
      temperatureDefault: [null, Validators.required],
      ph: [
        null,
        [
          Validators.required,
          Validators.pattern(REGEXP_PATTERNS.SIGNED_NUMBER_4_DECIMALS),
          NumericInputValidator.min(0),
        ],
      ],
      totalPressure: [
        null,
        [
          Validators.required,
          Validators.pattern(REGEXP_PATTERNS.SIGNED_NUMBER_4_DECIMALS),
          NumericInputValidator.min(0),
        ],
      ],
      totalPressureDefault: [null, Validators.required],
      totalCl: [
        null,
        [
          Validators.required,
          Validators.pattern(REGEXP_PATTERNS.SIGNED_NUMBER_4_DECIMALS),
          NumericInputValidator.min(0),
        ],
      ],
      h2s: [
        null,
        [
          Validators.required,
          Validators.pattern(REGEXP_PATTERNS.SIGNED_NUMBER_4_DECIMALS),
          InterchaneableUnitValidator.delimited(
            0,
            100, // 100% = 1,000,000 ppm
            this.h2sUnit,
            UnitMeasures.Percentage,
            this.unitMeasureService
          ),
        ],
      ],
      h2sDefault: [null, Validators.required],
      grade: [null, Validators.required],
      minYSRequired: [
        null,
        [
          Validators.required,
          Validators.pattern(REGEXP_PATTERNS.SIGNED_NUMBER_4_DECIMALS),
          InterchaneableUnitValidator.delimited(
            35,
            180,
            this.minYSRequiredUnit,
            UnitMeasures.Pressure,
            this.unitMeasureService,
            { defaultUnit: PressureUnits.Ksi }
          ),
        ],
      ],
      minYSRequiredDefault: [null, Validators.required],
    });

    merge(
      this.fg.get("temperatureDefault").valueChanges.pipe(pairwise()),
      this.fg.get("ph").valueChanges.pipe(pairwise()),
      this.fg.get("totalPressureDefault").valueChanges.pipe(pairwise()),
      this.fg.get("totalCl").valueChanges.pipe(pairwise()),
      this.fg.get("h2sDefault").valueChanges.pipe(pairwise()),
      this.fg.get("grade").valueChanges.pipe(pairwise()),
      this.fg.get("minYSRequiredDefault").valueChanges.pipe(pairwise())
    ).subscribe(([prevVal, currVal]) => {
      if (prevVal !== currVal) {
        this.model = null;
        this.oPph2s = null;
        this.defaultOPph2s = null;
        this.matSelGuidelinesComponentsService.updatePPH2SCra(this.oPph2s);
        this.matSelGuidelinesComponentsService.updateModelCra(this.model);

        // bug related to print validation
        if (!this.isLoading) {
          this.matSelGuidelinesComponentsService.setIsPrinteable(false);
          this.matSelGuidelinesComponentsService.fireFormChanges();
        }
      }
    });

    this.fg.valueChanges.subscribe(() => {
      this.matSelGuidelinesComponentsService.updateFgCra(this.fg.value);
      this.matSelGuidelinesComponentsService.updateFormCra(this.fg);
    });

    this.form.minYSRequiredDefault.valueChanges.subscribe((minYS) => {
      this.minYSSubject.next(minYS);
    });

    this.matSelGuidelinesComponentsService.loadCraObs.subscribe((load) => {
      this.isLoading = true;

      if (load) {
        this.fg?.patchValue({
          temperature: this.unitMeasureService.convertUnit(
            load.temperature,
            UnitMeasures.Temperature,
            this.temperatureUnit.value,
            false,
            4,
            { defaultUnit: TemperatureUnits.Celsius }
          ),
          totalPressure: this.unitMeasureService.convertUnit(
            load.total_pressure,
            UnitMeasures.Pressure,
            this.totalPressureUnit.value,
            false,
            4,
            { defaultUnit: PressureUnits.Kpa }
          ),
          minYSRequired: this.unitMeasureService.convertUnit(
            load.minYS,
            UnitMeasures.Pressure,
            this.minYSRequiredUnit.value,
            false,
            4,
            { defaultUnit: PressureUnits.Ksi }
          ),
          h2s: this.unitMeasureService.convertUnit(
            load.percentage_H2S,
            UnitMeasures.Percentage,
            this.h2sUnit.value,
            false,
            4,
            { defaultUnit: PercentageUnits.Percent }
          ),
          ph: load.ph,
          totalCl: load.total_Cl,
          grade: load.grade,
        });

        this.model = {
          pph2s: load.partial_pressure_H2S,
          criteria15156cracking: load.criteria,
          backgroundColor: load.background_color,
          recommendedGradesEnvi: Object.entries(load.recommended_grades_envi),
          recommendedGradesEnviMech: Object.entries(
            load.recommended_grades_envi_mech
          ),
          recommendedGradesEnviMap: load.recommended_grades_envi,
          recommendedGradesEnviMechMap: load.recommended_grades_envi_mech,
          recommendedGradesComment: load.recommended_grades_comment,
          sourConsiderations: load.sour_considerations,
        };

        if (this.areAllValuesNullOrUndefined(this.model)) this.model = null;

        this.defaultOPph2s = load.partial_pressure_H2S;
        this.oPph2s =
          load.partial_pressure_H2S || load.partial_pressure_H2S === 0
            ? this.unitMeasureService
                .convertUnit(
                  load.partial_pressure_H2S,
                  UnitMeasures.Pressure,
                  this.totalPressureUnit.value,
                  false,
                  4,
                  { defaultUnit: PressureUnits.Kpa }
                )
                .toFixed(4)
            : null;
        this.matSelGuidelinesComponentsService.updatePPH2SCra(this.oPph2s);

        this.matSelGuidelinesComponentsService.updateModelCra(this.model);
        this.updateAllControls(this.fg);
      } else this.onNew();
    });

    this.getGrades();

    this.minYSSubject.pipe(debounceTime(800)).subscribe((minYS) => {
      this.getGradesFiltered(minYS);
    });

    this.matSelGuidelinesComponentsService.resetValuesObs.subscribe(() => {
      this.onNew();
    });

    combineLatest([
      this.temperatureUnit.valueChanges.pipe(
        startWith(this.temperatureUnit.value)
      ),
      this.totalPressureUnit.valueChanges.pipe(
        startWith(this.totalPressureUnit.value)
      ),
      this.minYSRequiredUnit.valueChanges.pipe(
        startWith(this.minYSRequiredUnit.value)
      ),
      this.h2sUnit.valueChanges.pipe(
        startWith(this.h2sUnit.value)
      ),
    ]).subscribe(
      ([temperatureValue, totalPressureValue, minYSRequiredValue, h2sValue]) => {
        const units = {
          temperature: temperatureValue,
          totalPressure: totalPressureValue,
          minYSRequired: minYSRequiredValue,
          h2s: h2sValue,
        };

        this.oPph2s = this.oPph2s
          ? this.unitMeasureService
              .convertUnit(
                this.defaultOPph2s,
                UnitMeasures.Pressure,
                totalPressureValue,
                false,
                4,
                { defaultUnit: PressureUnits.Kpa }
              )
              .toFixed(4)
          : null;

        this.matSelGuidelinesComponentsService.updatePPH2SCra(this.oPph2s);

        this.unitPPH2S = this.unitMeasureService.getUnitSymbol(
          UnitMeasures.Pressure,
          totalPressureValue
        );
        this.matSelGuidelinesComponentsService.updateCraUnits(units);
      }
    );

    this.unitPPH2S = this.unitMeasureService.getUnitSymbol(
      UnitMeasures.Pressure,
      PressureUnits.Kpa
    );
  }

  areAllValuesNullOrUndefined(obj: any): boolean {
    return Object.values(obj).every(
      (value) =>
        value === null ||
        value === undefined ||
        (typeof value === "object" && Object.keys(value).length === 0)
    );
  }

  onNew() {
    this.fg.reset();
  }

  getGrades() {
    this.matSelGuidelinesService.getGradesCra().subscribe((grades) => {
      this.grades = grades;
      this.gradesFiltered = grades;
    });
  }

  getGradesFiltered(minYS?: number) {
    if (minYS) {
      this.matSelGuidelinesService.getGradesCra(minYS).subscribe((grades) => {
        this.gradesFiltered = grades;

        if (!this.gradesFiltered?.includes(this.form.grade.value))
          this.form.grade.setValue(null);
      });
    } else {
      this.gradesFiltered = this.grades;
      this.form.grade.setValue(null);
    }

    this.isLoading = false;
  }

  runModel() {
    if (this.fg.invalid)
      Swal.fire({
        title: "Action required",
        text: "Please complete all inputs to generate results",
        icon: "warning",
        confirmButtonColor: "#009",
        cancelButtonColor: "#aaa",
      });

    let request: RunModelCraRequest = {
      temperature: this.fg.value.temperatureDefault,
      totPressure: this.fg.value.totalPressureDefault,
      percentageH2S: this.fg.value.h2sDefault,
      minYS: this.fg.value.minYSRequiredDefault,
      pH: this.fg.value.ph,
      totalCl: this.fg.value.totalCl,
      grade: this.fg.value.grade,
      customerId: this.preSimulationService.getCurrentCustomerId(),
      countryId: this.preSimulationService.getCurrentCountryId(),
    };

    this.matSelGuidelinesService.runModelCra(request).subscribe((response) => {
      this.model = {
        pph2s: response.ppH2s?.toFixed(4),
        criteria15156cracking: response.criteria15156cracking,
        backgroundColor: response.backgroundColor,
        recommendedGradesEnvi: Object.entries(response.recommendedGradesEnvi),
        recommendedGradesEnviMech: Object.entries(
          response.recommendedGradesEnviMech
        ),
        recommendedGradesEnviMap: response.recommendedGradesEnvi,
        recommendedGradesEnviMechMap: response.recommendedGradesEnviMech,
        recommendedGradesComment: response.recommendedGradesComment,
        sourConsiderations: response.sourConsiderations,
      };

      this.defaultOPph2s = response.ppH2s;
      this.oPph2s =
        response.ppH2s || response.ppH2s === 0
          ? this.unitMeasureService
              .convertUnit(
                response.ppH2s,
                UnitMeasures.Pressure,
                this.totalPressureUnit.value,
                false,
                4,
                { defaultUnit: PressureUnits.Kpa }
              )
              .toFixed(4)
          : null;
      this.matSelGuidelinesComponentsService.updatePPH2SCra(this.oPph2s);

      this.matSelGuidelinesComponentsService.updateModelCra(this.model);
    });
  }

  rangePopUp() {
    this.rangeCra = true;
  }

  closeModal() {
    this.rangeCra = false;
  }

  updateAllControls(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormGroup) {
        this.updateAllControls(control);
      } else {
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  get form() {
    return this.fg.controls;
  }

}
